<template>
	<AdminLayout>
		<div class="flex flex-row mb-4">
			<p class="admin-center-subtitle">Organizations</p>
			<SubmitButton v-if="authStore.isPortalAdmin || authStore.canCreateOrganization" label="Create New" @click="onAddNewClicked" size="small"/>
		</div>

		<PortalDataTable
			ref="table"
			:endpoints="endpoints"
			@on-data-loaded="onDataLoaded"
		>
			<template #organization_name="data">
				<p class="whitespace-pre-wrap xl:whitespace-nowrap w-fit" :class="[{ 'line-through': data.value.deleted_at, 'endpoint-clickable-text' : canEditOrganization(data.value.id) }]"
				   @click.prevent="onOrganizationClicked(data.value.id)">
					{{ data.value.organization_name }}<br>{{ (data.value.organization_name_dba ? "(" + data.value.organization_name_dba + ")" : "") }}
				</p>
			</template>

			<template #sort_address="data">
				<div :class="[{ 'line-through': data.value.deleted_at }]">
					{{ data.value.street }}<br>{{ data.value.city }}, {{ data.value.state }} {{ data.value.zip }}
				</div>
			</template>

			<template #status="data">
				<div class="flex w-full justify-center whitespace-nowrap">
					<EndpointTableListBox
						v-if="canEditStatus"
						:record-id="data.value.id"
						:items="statusColumnData"
						v-model="data.value.status"
						:disabled="!canEditStatus"
						:showSelectedCheckmark="false"
						width="w-26"
						@onItemSelected="onStatusItemSelected"
						:displayed-item-c-s-s="{ 'Disabled': '!bg-disabled-subtle !text-label', 'Active': '!bg-primary-lighter !text-label'}"
					/>
					<div v-else class="rounded px-2 py-1 text-center" :class="{ 'bg-primary-lighter': data.value.status.text === 'Active', 'bg-disabled-subtle': data.value.status.text === 'Disabled' }">{{ data.value.status.text }}</div>
				</div>
			</template>
		</PortalDataTable>
	</AdminLayout>

	<Teleport to="html">
		<CreateOrganizationModal :show="orgCreateStore.getShowModal" />
	</Teleport>

	<Teleport to="html">
		<OrganizationEditModal :show="showEditModal" :org_id="selOrgId" @evCloseOrgEditModal="onEditModalDismiss"/>
	</Teleport>
</template>

<script setup lang="js">
import { computed, ref } from 'vue'
import AdminLayout from '@/layouts/AdminLayout.vue'
import OrganizationEditModal from '@/views/Admin/Organizations/OrganizationEditModal.vue'
import { useRoute } from 'vue-router'
import CreateOrganizationModal from '@/views/Organizations/CreateOrganizationModal.vue'
import { useCreateOrganizationStore } from '@/stores/organizationStore.js'
import SubmitButton from '@/components/Forms/Buttons/SubmitButton.vue'
import { useAuthStore } from '@/stores/authStore.js'
import EndpointTableListBox from '@/components/Tables/EndpointTableListBox.vue'
import { organizationsApi } from '@/api/Organizations/OrganizationsApi.js'
import { Toaster } from '@/models/utils/Toaster.js'
import PortalDataTable from '@/components/DataTable/PortalDataTable.vue'
import { DataTableEndpointDefinition } from '@/models/datatable/DataTableEndpointDefinition.js'

const authStore = useAuthStore()
const showEditModal = ref(false)
const selOrgId = ref(0)
const route = useRoute()
const toaster = new Toaster();

const canEditStatus = ref(authStore.isPortalAdmin)
const editableOrgIds = ref(authStore.getEditableOrgs)

const statusColumnData = computed(() =>
{
	let items = [];

	items.push({ id: 1, text:'Active' })
	items.push({ id: 2, text:'Disabled' })

	return items;
})

const orgCreateStore = useCreateOrganizationStore()
const table = ref(null)
const columns = [
	{ field: "organization_name", title: "Name" },
	{ field: "sort_address", title: "Address" }, // Sorts on street + city
	{ field: "status", title: "Status", sort: false, width: '1%' }
]

const endpoints = [
	new DataTableEndpointDefinition(
		{
			endpointRoute: 'organizations/organizations',
			initialSortColumn: 'organization_name',
			searchPlaceholder: 'Search by name',
			columns: columns,
			fullTextSearch: true,
		}
	)
]

function onDataLoaded() {
	const orgIdToShow = route.query.show_org_id
	if (orgIdToShow) {
		onOrganizationClicked(orgIdToShow)
	}
}

/**
 * Checks if the user can edit the organization for the user
 * @param p_org_id
 * @returns {boolean}
 */
function canEditOrganization(p_org_id)
{
	if (authStore.isPortalAdmin) return true;

	if (editableOrgIds.value.includes(p_org_id))
		return true;

	return false
}

/**
 * Show the Add Org Dialog
 */
function onAddNewClicked()
{
	orgCreateStore.toggleCreateModal(true)
}

/**
 * Loads data for a single organization and shows the modal dialog
 * @param p_id Organizations.ID
 */
function onOrganizationClicked(p_id)
{
	try
	{
		if (!canEditOrganization(p_id)) return;

		selOrgId.value = p_id
		showEditModal.value = true
	}
	catch (e)
	{
		console.error('onOrganizationClicked:error', e)
	}
}

function onEditModalDismiss(reload) {
	showEditModal.value = false

	// Remove the query param without refreshing the page
	const url = new URL(window.location.href);
	const params = url.searchParams;
	params.delete('show_org_id');
	history.replaceState(null, '', url.toString());

	if (reload) {
		if (table.value) {
			toaster.success("Organization successfully updated!")
			table.value.loadData()
		} else {
			location.reload()
		}
	}
}

function onStatusItemSelected(p_item)
{
	if (p_item.text === "Active")
	{
		restore(p_item);
	}
	else
	{
		deactivate(p_item);
	}
}

function restore(p_item)
{
	organizationsApi.restore(p_item.record_id, () =>
	{
		toaster.success("Organization Restored")
	}, (e) =>
	{
		e.showErrorBanner()
	})
}

function deactivate(p_item)
{

	organizationsApi.delete(p_item.record_id, () =>
	{
		toaster.success("Organization Disabled")
	}, (e) =>
	{
		e.showErrorBanner()
	})
}

</script>